import { useEffect, useRef, useState } from 'react';
import { api, withQuery } from '../api.js';
import Suggestions, { useSuggestKeys } from './Suggestions.jsx';

/**
 * Търсене на стока — както `Lookup`, но с групите като хедъри в списъка.
 *
 * Стоковата номенклатура е няколкостотин реда с наподобяващи се имена
 * (десетки варианти „100BRATN..." на пръв поглед еднакви) — групата е
 * това, което помага да се разпознае търсеното от пръв поглед, вместо да
 * се чете ред по ред. Същият шаблон, установен в „Продажба/доставка"
 * (виж `Suggestions.jsx`), само в общ вид за навсякъде другаде, където се
 * търси стока.
 */
export default function ProductLookup({
  url = '/api/products', value, label, onPick, placeholder, params, style,
}) {
  const [text, setText] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  const extra = JSON.stringify(params ?? {});
  const izberi = (it) => { onPick(it.id, it.name); setOpen(false); setText(''); };
  const keys = useSuggestKeys(items, izberi, (it) => it.group_name, open && items.length > 0);

  useEffect(() => {
    if (!open || text.trim().length < 2) { setItems([]); return undefined; }
    const timer = setTimeout(() => {
      api.get(withQuery(url, { ...JSON.parse(extra), q: text.trim(), limit: 20 }))
        .then((res) => setItems(res.items ?? res ?? []))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(timer);
  }, [text, open, url, extra]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  if (value) {
    return (
      <span className="picked">
        {label}
        <button className="link" onClick={() => { onPick(null, null); setText(''); }} title="махни">×</button>
      </span>
    );
  }

  return (
    <span className="lookup" ref={box} style={{ position: 'relative', display: 'block', ...style }}>
      <input
        value={text}
        placeholder={placeholder ?? 'търсене на стока'}
        autoComplete="off"
        style={{ width: '100%' }}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); else keys.onKeyDown(e); }}
      />
      {open && items.length > 0 && (
        <Suggestions
          items={items}
          activeId={keys.activeId}
          groupBy={(it) => it.group_name}
          render={(it) => (
            <>
              {it.name}
              {it.code ? <span className="muted"> · {it.code}</span> : null}
            </>
          )}
          onPick={izberi}
        />
      )}
    </span>
  );
}
